import { useRef, useState } from 'react';
import type { WeatherCardData, WeatherConditionCode } from '../../lib/conversationsApi';
import './weatherCard.css';

type WeatherMetric = 'temperature' | 'precipitation' | 'wind';
type TemperatureUnit = 'c' | 'f';

function displayTemperature(celsius: number, unit: TemperatureUnit) {
  const value = unit === 'c' ? celsius : (celsius * 9 / 5) + 32;
  return `${Math.round(value)}°`;
}

function WeatherGlyph({ code, compact = false }: { code: WeatherConditionCode; compact?: boolean }) {
  const cloud = code === 'cloudy' || code === 'partly_cloudy' || code === 'rain' || code === 'storm' || code === 'snow';
  return (
    <svg className={`ozira-weather-glyph${compact ? ' is-compact' : ''}`} viewBox="0 0 64 64" aria-hidden="true">
      {(code === 'clear' || code === 'partly_cloudy' || code === 'unknown') && (
        <g className="ozira-weather-sun">
          <circle cx="27" cy="27" r="12" />
          <path d="M27 5v7M27 42v7M5 27h7M42 27h7M11.5 11.5l5 5M37.5 37.5l5 5M42.5 11.5l-5 5M16.5 37.5l-5 5" />
        </g>
      )}
      {cloud && <path className="ozira-weather-cloud" d="M18 47h29a10 10 0 0 0 1-19.9A15 15 0 0 0 19.4 24 11.5 11.5 0 0 0 18 47Z" />}
      {code === 'rain' && <path className="ozira-weather-detail" d="m23 52-3 7M34 52l-3 7M45 52l-3 7" />}
      {code === 'storm' && <path className="ozira-weather-bolt" d="m34 48-7 10h7l-3 6 12-13h-7l3-3Z" />}
      {code === 'snow' && <path className="ozira-weather-detail" d="M24 51v10M19.5 53.5l9 5M28.5 53.5l-9 5M43 51v10M38.5 53.5l9 5M47.5 53.5l-9 5" />}
      {code === 'fog' && <path className="ozira-weather-detail" d="M10 24h42M5 34h46M13 44h40" />}
      {code === 'wind' && <path className="ozira-weather-detail" d="M7 23h35c8 0 8-11 1-11-4 0-6 2-6 5M7 34h45c8 0 8 12 0 12-4 0-6-2-6-5M7 45h24" />}
    </svg>
  );
}

export function WeatherCard({ weather, sourceLabel = 'Live weather' }: { weather: WeatherCardData; sourceLabel?: string }) {
  const [unit, setUnit] = useState<TemperatureUnit>('c');
  const [metric, setMetric] = useState<WeatherMetric>('temperature');
  const hourlyRef = useRef<HTMLDivElement>(null);

  const scrollHours = (direction: -1 | 1) => {
    const track = hourlyRef.current;
    if (!track) return;
    track.scrollBy({ left: direction * Math.max(320, track.clientWidth * 0.8), behavior: 'smooth' });
  };

  const hourValue = (hour: WeatherCardData['hourly'][number]) => {
    if (metric === 'precipitation') return `${Math.round(hour.precipitationPercent)}%`;
    if (metric === 'wind') return `${Math.round(hour.windKph)} km/h`;
    return displayTemperature(hour.temperatureC, unit);
  };

  return (
    <section className="ozira-weather-card" aria-label={`Weather for ${weather.location}`}>
      <div className="ozira-weather-brandline">
        <span className="ozira-weather-live-dot" />
        <span>OZIRA Live</span>
        <span className="ozira-weather-verified">Verified</span>
      </div>

      <div className="ozira-weather-heading">
        <div>
          <p className="ozira-weather-date">{weather.dateLabel} · Feels {displayTemperature(weather.feelsLikeC, unit)}</p>
          <h3>{weather.location}</h3>
        </div>
        <div className="ozira-weather-unit" aria-label="Temperature unit">
          <button type="button" className={unit === 'c' ? 'is-active' : ''} aria-pressed={unit === 'c'} onClick={() => setUnit('c')}>°C</button>
          <button type="button" className={unit === 'f' ? 'is-active' : ''} aria-pressed={unit === 'f'} onClick={() => setUnit('f')}>°F</button>
        </div>
      </div>

      <div className="ozira-weather-current">
        <div className="ozira-weather-icon-wrap"><WeatherGlyph code={weather.conditionCode} /></div>
        <div>
          <div className="ozira-weather-temperature">{displayTemperature(weather.temperatureC, unit)}</div>
          <p className="ozira-weather-condition">{weather.condition} · H:{displayTemperature(weather.highC, unit)} L:{displayTemperature(weather.lowC, unit)}</p>
        </div>
      </div>

      <div className="ozira-weather-facts" aria-label="Current weather details">
        <span className="ozira-weather-fact">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 2.69s-7 7.16-7 11.31a7 7 0 0 0 14 0c0-4.15-7-11.31-7-11.31z" /></svg>
          {Math.round(weather.precipitationPercent)}%
        </span>
        <span className="ozira-weather-fact">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2.5 2.5 0 1 1 19.5 12H2" /></svg>
          {Math.round(weather.windKph)} km/h{weather.windDirection ? ` ${weather.windDirection}` : ''}
        </span>
        {weather.humidityPercent !== null ? (
          <span className="ozira-weather-fact">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3-3.4-4-5.5c-1 2.1-2 4-4 5.5S5 13 5 15a7 7 0 0 0 7 7z" /></svg>
            {Math.round(weather.humidityPercent)}%
          </span>
        ) : null}
      </div>

      <div className="ozira-weather-tabs" role="tablist" aria-label="Hourly weather metric">
        {(['temperature', 'precipitation', 'wind'] as WeatherMetric[]).map((value) => (
          <button key={value} type="button" role="tab" aria-selected={metric === value} className={metric === value ? 'is-active' : ''} onClick={() => setMetric(value)}>
            {value[0].toUpperCase() + value.slice(1)}
          </button>
        ))}
      </div>

      <div className="ozira-weather-hourly-shell">
        <button type="button" className="ozira-weather-scroll is-left" aria-label="Show earlier forecast hours" onClick={() => scrollHours(-1)}>‹</button>
        <div className="ozira-weather-hourly" ref={hourlyRef} tabIndex={0} aria-label="24-hour forecast. Scroll horizontally to see every hour.">
          {weather.hourly.map((hour, index) => (
            <div className="ozira-weather-hour" key={`${hour.time}-${index}`}>
              <strong>{hour.time}</strong>
              <WeatherGlyph code={hour.conditionCode} compact />
              <span className={`ozira-weather-hour-value is-${metric}`}>{hourValue(hour)}</span>
              {metric === 'temperature' ? <small>{Math.round(hour.precipitationPercent)}% rain</small> : null}
            </div>
          ))}
        </div>
        <button type="button" className="ozira-weather-scroll is-right" aria-label="Show later forecast hours" onClick={() => scrollHours(1)}>›</button>
      </div>

      {weather.daily?.length ? (
        <div className="ozira-weather-daily" aria-label="5-day forecast">
          {weather.daily.map((day, index) => (
            <div className="ozira-weather-day" key={`${day.date}-${index}`}>
              <strong>{day.dayLabel}</strong>
              <WeatherGlyph code={day.conditionCode} compact />
              <span className="ozira-weather-day-precip">{Math.round(day.precipitationPercent)}%</span>
              <span className="ozira-weather-day-range">
                <span className="ozira-weather-day-high">{displayTemperature(day.highC, unit)}</span>
                <span className="ozira-weather-day-low">{displayTemperature(day.lowC, unit)}</span>
              </span>
            </div>
          ))}
        </div>
      ) : null}

      <div className="ozira-weather-footer">
        <span>{sourceLabel}</span>
        <span>{weather.hourly.length}-hour forecast</span>
      </div>
    </section>
  );
}
